import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, History, RotateCcw, Trash2 } from "lucide-react";
import { api, messageErreur } from "@/lib/api";
import { formatDateHeure } from "@/lib/format";
import { Confirmer } from "@/components/migration/Confirmer";

export const Versions = ({ o, onMaj }) => {
  const revenir = async (n) => {
    try { const { data } = await api.post(`/outils/${o.id}/version`, { n }); onMaj(data); toast.success(`Retour à la version ${n}.`); } catch (x) { toast.error(messageErreur(x)); }
  };
  return (
    <section className="at-card p-5 space-y-2" data-testid="tool-versions">
      <p className="font-bold flex items-center gap-2"><History size={16} />Versions</p>
      {[...o.versions].reverse().map((v) => (
        <div key={v.n} className="at-soft p-3 text-sm space-y-1" data-testid={`tool-version-${v.n}`}>
          <div className="flex justify-between gap-2 items-center">
            <b>v{v.n}{v.n === o.version_active ? " · affichée" : ""}</b>
            {v.n !== o.version_active && <button type="button" onClick={() => revenir(v.n)} className="text-xs font-semibold min-h-[44px] flex items-center gap-1" data-testid={`tool-version-restore-${v.n}`}><RotateCcw size={13} />Revenir</button>}
          </div>
          <p className="break-words">{v.consigne}</p>
          {v.notes && <p className="text-xs at-muted">{v.notes}</p>}
          <p className="text-xs at-muted">{formatDateHeure(v.created_at)}</p>
        </div>
      ))}
    </section>
  );
};

export const Garder = ({ o, onMaj }) => {
  const [nom, setNom] = useState(o.nom);
  const [aut, setAut] = useState(o.actions_declarees.map((a) => a.code));
  const garder = async () => {
    try { const { data } = await api.post(`/outils/${o.id}/garder`, { nom, actions_autorisees: aut }); onMaj(data); toast.success(o.type === "vue" ? "Vue enregistrée et ajoutée au catalogue." : "Outil enregistré."); }
    catch (x) { toast.error(messageErreur(x)); }
  };
  return (
    <section className="at-card p-5 space-y-3" data-testid="tool-keep">
      <p className="font-bold">Enregistrer cet outil</p>
      <input value={nom} onChange={(e) => setNom(e.target.value)} className="at-input" data-testid="tool-keep-name" />
      {o.actions_declarees.length > 0 ? (
        <div className="space-y-1">
          <p className="text-sm">Actions d'écriture déclarées par l'outil : cochez celles que vous autorisez.</p>
          {o.actions_declarees.map((a) => (
            <label key={a.code} className="at-soft px-3 min-h-[44px] flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" className="w-5 h-5" checked={aut.includes(a.code)} onChange={() => setAut(aut.includes(a.code) ? aut.filter((x) => x !== a.code) : [...aut, a.code])} data-testid={`tool-keep-action-${a.code}`} />{a.label}
            </label>
          ))}
        </div>
      ) : <p className="text-sm at-muted">Cet outil ne fait que lire des données.</p>}
      <button type="button" onClick={garder} className="at-btn at-btn-primary w-full" data-testid="tool-keep-button"><Check size={16} />Garde-la</button>
    </section>
  );
};

const LIEUX = [["accueil", "Accueil"], ["fiche", "Écran fiche (reçoit la fiche ouverte)"], ["dock", "Dock"]];

export const Reglages = ({ o, onMaj }) => {
  const navigate = useNavigate();
  const regler = async (corps, msg) => { try { await api.patch(`/outils/${o.id}`, corps); toast.success(msg); onMaj(); } catch (x) { toast.error(messageErreur(x)); } };
  const supprimer = async () => { try { const { data } = await api.delete(`/outils/${o.id}`); toast.success(data.message); navigate("/outils"); } catch (x) { toast.error(messageErreur(x)); } };
  return (
    <section className="at-card p-5 space-y-3" data-testid="tool-settings">
      <p className="font-bold">Réglages</p>
      <button type="button" onClick={() => regler({ statut: o.statut === "actif" ? "pause" : "actif" }, o.statut === "actif" ? "Outil mis en pause." : "Outil activé.")} className="at-btn at-btn-ghost w-full" data-testid="tool-toggle-status">
        {o.statut === "actif" ? "Mettre en pause" : "Activer"}
      </button>
      {o.type === "vue" ? <p className="text-sm">Vue ajoutée au catalogue : choisissez où l'afficher dans <Link to="/parametres/vues" className="underline font-semibold" data-testid="tool-views-link">Paramètres › Vues</Link>.</p> : LIEUX.map(([c, l]) => (
        <label key={c} className="at-soft px-3 min-h-[44px] flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" className="w-5 h-5" checked={!!o.epingles[c]} onChange={(e) => regler({ epingles: { ...o.epingles, [c]: e.target.checked } }, "Épingle mise à jour.")} data-testid={`tool-pin-${c}`} />Épingler : {l}
        </label>
      ))}
      <p className="text-xs at-muted">Actions validées : {o.actions_autorisees.length ? o.actions_declarees.filter((a) => o.actions_autorisees.includes(a.code)).map((a) => a.label).join(", ") : "aucune (lecture seule)"}</p>
      {!o.modele && <Confirmer titre="Mettre cet outil à la corbeille ?" texte="Il reste restaurable 30 jours depuis Paramètres › Corbeille." testId="tool-delete" onOk={supprimer} danger><Trash2 size={16} />Mettre à la corbeille</Confirmer>}
    </section>
  );
};
